import React, { useEffect, useState } from 'react';
import AdminKeyGate from '../components/AdminKeyGate';
import MapStage from '../components/pitwall/MapStage';
import '../components/pitwall/PitwallVisual.css';
import { setAdminKey, getAdminRaces, getLiveLeaderboard, getTrackOutline } from '../api';
import useLiveMap from '../useLiveMap';
import useRacePlayback from '../useRacePlayback';
import { getVisualPreview } from '../pitwallVisualPreview';

const POLL_INTERVAL = 15_000;

function findFeaturedRace(races) {
  const inWindow = races.find(r => {
    if (!r.locked || r.cancelled || r.official_top10 || !r.race_date) return false;
    const hours = (Date.now() - new Date(r.race_date)) / 36e5;
    return hours >= 0 && hours < 12;
  });
  return inWindow ?? races.find(r => r.locked && !r.cancelled && !r.official_top10)
    ?? races.find(r => !r.cancelled && !r.official_top10 && r.video_url) ?? null;
}

function PreviewMap() {
  const PREVIEW = getVisualPreview(window.location.search);
  return (
    <MapScreen race={PREVIEW.race} data={PREVIEW.data} outline={PREVIEW.outline}
      carPositions={PREVIEW.positions} source={PREVIEW.source} stale={PREVIEW.stale} />
  );
}

export default function PitWallPageV3() {
  const isPreview = new URLSearchParams(window.location.search).has('preview');
  const [adminKey, setAdminKeyState] = useState(() => {
    if (isPreview) return null; // Never load credentials in fixture mode.
    const stored = sessionStorage.getItem('f1tips_adminkey');
    if (stored) setAdminKey(stored);
    return stored;
  });
  if (isPreview) return <PreviewMap />;
  if (!adminKey) {
    return <div className="pw3-access"><AdminKeyGate onKey={key => { setAdminKey(key); setAdminKeyState(key); }} /></div>;
  }
  return <LiveMap onAuthError={() => {
    sessionStorage.removeItem('f1tips_adminkey');
    setAdminKey(null);
    setAdminKeyState(null);
  }} />;
}

function LiveMap({ onAuthError }) {
  const [race, setRace] = useState(null);
  const [raceLoading, setRaceLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [data, setData] = useState(null);
  const [outline, setOutline] = useState(null);
  const playback = useRacePlayback(authError ? null : race?.id);
  const pubMode = playback.mode !== 'live';
  const mapSnapshot = useLiveMap(authError || pubMode ? null : race?.id);

  // One bounded, non-overlapping discovery loop even when no race is selected.
  // Re-evaluate all races on every result so a new lock, cancellation, deletion,
  // or admin selection change is reflected without reopening the screen.
  useEffect(() => {
    if (authError) return undefined;
    let active = true;
    let timer;
    async function discover() {
      try {
        const races = await getAdminRaces();
        if (active) setRace(previous => {
          const list = Array.isArray(races) ? races : [];
          // Do not select a newer race automatically after the pub clock's race ends.
          if (previous && pubMode) return list.find(item => item.id === previous.id) || previous;
          return findFeaturedRace(list);
        });
      } catch (error) {
        if (active && error.response?.status === 401) {
          setAuthError(true);
          return;
        }
        // Transient error: retain the last known race and retry at normal cadence.
      } finally {
        if (active) {
          setRaceLoading(false);
          // A rejected key stops discovery; React cleans up this effect when
          // authError changes. Never overlap calls, even on slow connections.
          timer = setTimeout(discover, POLL_INTERVAL);
        }
      }
    }
    discover();
    return () => { active = false; clearTimeout(timer); };
  }, [authError, pubMode]);

  useEffect(() => {
    if (!race?.id || authError || pubMode) return undefined;
    let active = true;
    let timer;
    setData(null); // Never show the previous race's standings after selection changes.
    async function refresh() {
      try {
        const result = await getLiveLeaderboard(race.id);
        if (active) setData(result);
      } catch (error) {
        if (active && error.response?.status === 401) setAuthError(true);
      } finally {
        if (active) timer = setTimeout(refresh, POLL_INTERVAL);
      }
    }
    refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [race?.id, authError, pubMode]);

  useEffect(() => {
    if (!race?.id || authError) return undefined;
    let active = true;
    setOutline(null);
    getTrackOutline(race.id).then(result => { if (active) setOutline(result); })
      .catch(error => {
        if (active && error.response?.status === 401) setAuthError(true);
      });
    return () => { active = false; };
  }, [race?.id, authError]);

  if (authError) {
    return <div className="pw3-access"><div className="pw3-access-message">Admin key rejected<button onClick={onAuthError}>RE-ENTER KEY</button></div></div>;
  }
  if (raceLoading) return <div className="pw3-access">INITIALISING TRACK MAP...</div>;
  if (!race) return <div className="pw3-access">NO LIVE RACE — PIT WALL ACTIVATES WHEN A RACE IS LOCKED</div>;

  const frame = playback.frame;
  const selectedData = pubMode ? (frame?.dashboard ? {
    ...frame.dashboard, liveTop10: frame.dashboard.top10 || [], totalLaps: frame.race.totalLaps,
  } : null) : data;
  return (
    <MapScreen key={`${race.id}:${playback.mode}:${frame?.epoch ?? ''}:${frame?.settingsVersion ?? ''}`}
      race={pubMode ? { id: race.id, season: race.season, round: race.round, name: race.name, circuit: race.circuit } : race} data={selectedData} outline={outline}
      carPositions={pubMode ? frame?.map || {} : mapSnapshot.carPositions}
      source={pubMode ? 'pub' : mapSnapshot.source}
      stale={pubMode ? (playback.frame?.mapStatus || playback.status) !== 'ready' : mapSnapshot.stale}
      presentationTime={pubMode ? frame?.targetMs ?? 0 : Date.now()}
      positionStatusLabel={pubMode ? `TV SYNC · ${(playback.frame?.mapStatus || playback.status).toUpperCase()}` : undefined} />
  );
}

// Prop-only presentation: supervisor may replace the Live adapter's race/data/
// outline/mapSnapshot values with the shared delayed-TV playback state.
export function MapScreen({ race, data, outline, carPositions, source, stale, presentationTime = Date.now(), positionStatusLabel }) {
  const lastControl = data?.raceControl?.slice(-1)[0];
  const lastSafety = data?.raceControl?.slice().reverse().find(message => message.category === 'SafetyCar' || message.category === 'Vsc');
  const oldSafety = lastSafety && (presentationTime - new Date(lastSafety.date).getTime()) > 3 * 60 * 1000;
  const status = oldSafety ? 'clear' : data?.trackStatus;
  const activeControl = ['red', 'sc', 'sc_ending', 'vsc', 'vsc_ending', 'chequered'].includes(status);
  const bannerColours = {
    red: '#c00020', sc: '#92400e', sc_ending: '#92400e',
    vsc: '#1e3a5f', vsc_ending: '#1e3a5f', chequered: '#1b1b1b',
  };
  return (
    <div className="pw3-shell" style={{ gridTemplateRows: `${activeControl ? 'var(--pw3-banner-height,64px)' : '0px'} var(--pw3-header-height, 78px) minmax(0,1fr) var(--pw3-footer-height, 42px)` }}>
      <div className="pw3-banner" style={{ backgroundColor: bannerColours[status] || 'transparent' }}>
        {activeControl ? (lastControl?.message || status.toUpperCase()) : null}
      </div>
      <header className="pw3-header">
        <div className="pw3-title-wrap">
          <div className="pw3-eyebrow">{race.season} · Round {race.round} · Track map</div>
          <div className="pw3-title" title={race.name}>{race.name}</div>
        </div>
        <div className="pw3-summary">
          <div><span className="pw3-detail-label">Lap</span><span className="pw3-detail-value">{data?.currentLap ?? '—'}{data?.totalLaps ? ` / ${data.totalLaps}` : ''}</span></div>
          <div><span className="pw3-detail-label">Track</span><span className="pw3-detail-value">{data?.weather?.trackTemp != null ? `${Math.round(data.weather.trackTemp)}°C` : '—'}</span></div>
        </div>
      </header>
      <MapStage outline={outline} carPositions={carPositions} teamColours={data?.teamColours}
        topOrder={data?.liveTop10} circuit={race.circuit} source={source} stale={stale}
        statusLabel={positionStatusLabel} />
      <footer className="pw3-footer"><span>BRYGGERIET ÅNGKVARN · PIT WALL 3</span><span>TRACK POSITIONS · SHARED RACE FEED</span></footer>
    </div>
  );
}
